import React, { PropsWithChildren, ReactNode } from "react";
import styled, { css } from "styled-components";
import { Copy, EllipsisVertical } from "lucide-react";
import { hexToRGB } from "@shared/utils/misc";
import { Surface } from "common/NewLook";
import {
  iconButtonStyles,
  newLookTableStyles,
  tabularNums,
} from "features/affiliation/NewLook/newLookUtils";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";

/**
 * Card header: title (and extras) on the left, controls on the right. Kept
 * for the payout methods list (features/payouts); affiliation panels use
 * common/NewLook/Panel.
 */
export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px 12px;
  flex-wrap: wrap;
  min-height: 28px;
  margin-bottom: 12px;
`;
export const CardTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  min-height: 28px;
  margin: 0;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 14.5px;
  line-height: 1.35;
  font-weight: ${(p) => p.theme.typography.fontWeights.SEMI_BOLD};
`;

interface CopyValueProps {
  onClick: () => void;
  /** Accent text, for links. */
  isLink?: boolean;
  /** Ink figure text, e.g. a customer ID. */
  strong?: boolean;
  /** Meta size, e.g. the URL under a link's name. */
  small?: boolean;
  maxWidth?: number;
  title?: string;
  children: ReactNode;
}

/** A value that copies itself on click, with a quiet trailing copy icon. */
export const CopyValue = ({
  onClick,
  isLink,
  strong,
  small,
  maxWidth,
  title,
  children,
}: CopyValueProps) => (
  <CopyButton
    type="button"
    className="nl-copy"
    $isLink={isLink}
    $strong={strong}
    $small={small}
    title={title}
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    <span style={{ maxWidth }}>{children}</span>
    <Copy size={small ? 12 : 14} strokeWidth={1.75} />
  </CopyButton>
);

/** The "more actions" trigger of a table row or list row. */
export const MenuTrigger = styled.span.attrs({
  role: "button",
  tabIndex: 0,
  // Opens the menu from the keyboard too (the dropdown listens to clicks).
  onKeyDown: (event: React.KeyboardEvent<HTMLSpanElement>) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    event.currentTarget.click();
  },
  children: <EllipsisVertical size={16} strokeWidth={1.75} />,
})<{ $large?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: ${NEW_LOOK_RADIUS.control}px;
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  cursor: pointer;
  transition: background-color 120ms, color 120ms;
  &:hover,
  &.ant-dropdown-open {
    background: ${(p) => getNewLookTokens(p.theme).well};
    box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
  ${(p) =>
    p.$large &&
    css`
      width: 40px;
      height: 40px;
      margin: -8px -10px -8px 0;
    `}
`;

const CopyButton = styled.button<{
  $isLink?: boolean;
  $strong?: boolean;
  $small?: boolean;
}>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 100%;
  min-width: 0;
  margin: -2px -6px;
  padding: 2px 6px;
  border: 0;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: transparent;
  font: inherit;
  text-align: left;
  cursor: pointer;
  ${(p) => (p.$small ? newLookType.meta : newLookType.body)};
  font-variant-numeric: tabular-nums;
  color: ${(p) => {
    const tokens = getNewLookTokens(p.theme);
    if (p.$isLink) return tokens.accent;
    return p.$strong ? tokens.ink : tokens.text;
  }};
  ${(p) =>
    p.$strong &&
    css`
      ${newLookType.figure};
    `}
  transition: background-color 120ms;
  > span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > svg {
    flex: none;
    color: ${(p) => getNewLookTokens(p.theme).muted};
    transition: color 120ms, opacity 120ms;
  }
  &:hover {
    background: ${(p) => getNewLookTokens(p.theme).well};
    box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
    > svg {
      color: ${(p) => getNewLookTokens(p.theme).accent};
    }
  }
  &:focus-visible {
    outline: 2px solid ${(p) => getNewLookTokens(p.theme).focus};
    outline-offset: 1px;
  }
`;

/* ---- Kept for features/payouts (invoices), which still uses them ---- */

/** A quiet count next to a title. */
export const CountPill = styled.span`
  padding: 1px 8px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => p.theme.colors.lightBlue};
  color: ${(p) => p.theme.colors.primaryBlue};
  font-size: 11.5px;
  line-height: 18px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  ${tabularNums};
`;

interface StatusPillProps {
  color?: string;
}

/** A small status label with a dot, tinted by the status color. */
export const StatusPill = ({
  color,
  children,
}: PropsWithChildren<StatusPillProps>) => (
  <StatusPillWrapper $color={color}>
    <span />
    {children}
  </StatusPillWrapper>
);
const StatusPillWrapper = styled.span<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  font-size: 12px;
  line-height: 18px;
  font-weight: ${(p) => p.theme.typography.fontWeights.MEDIUM};
  white-space: nowrap;
  color: ${(p) => p.$color ?? p.theme.colors.secondaryText};
  background: ${(p) =>
    p.$color ? hexToRGB(p.$color, 0.12) : p.theme.colors.cardLightBgColor};
  > span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }
`;

/**
 * A bordered surface holding a table with its header row. The header row is
 * 28px like every card header (the 32px search overhangs it), so titles line
 * up with the cards next to the table.
 */
export const TableSurface = styled(Surface)`
  padding: 20px 20px 8px;
  > div:first-child > div:last-child {
    margin-block: -2px;
  }
  ${newLookTableStyles};
`;
/** The previous row-menu trigger (invoices). */
export const KebabTrigger = styled.span.attrs({
  role: "button",
  children: <EllipsisVertical size={16} strokeWidth={1.75} />,
})<{ $large?: boolean }>`
  ${iconButtonStyles};
  ${(p) =>
    p.$large &&
    css`
      width: 40px;
      height: 40px;
      margin: -8px -10px -8px 0;
    `}
`;
